/**
 * Section B3 Page
 * B.3.專案邊界
 * 三頁籤：(1)減量目標-富文本 (2)基準減量-JSON CRUD (3)專案減量-JSON CRUD
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB3Service, { SectionB3Detail, EmissionSource, GhgItem } from '../services/sectionB3Service';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionB3Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 頁籤 ==========
  const [activeTab, setActiveTab] = useState<number>(1);

  // ========== 資料狀態 ==========
  const [sectionB3Data, setSectionB3Data] = useState<SectionB3Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== 表單狀態 ==========
  const [propose, setPropose] = useState('');
  const [baselineEmissions, setBaselineEmissions] = useState<EmissionSource[]>([]);
  const [projectEmissions, setProjectEmissions] = useState<EmissionSource[]>([]);
  const [originalData, setOriginalData] = useState({ propose: '', baseline: [] as EmissionSource[], project: [] as EmissionSource[] });

  // ========== GHG 選項 ==========
  const [ghgOptions, setGhgOptions] = useState<SystemCode[]>([]);

  // ========== Modal 狀態 ==========
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalSource, setModalSource] = useState('');
  const [modalGhg, setModalGhg] = useState<GhgItem[]>([]);

  // ========== 頁籤定義 ==========
  const tabs = [
    { key: 1, label: i18n.language === 'zh-TW' ? 'B.3.1 減量目標與目的' : 'B.3.1 The project boundary of the proposed project' },
    { key: 2, label: i18n.language === 'zh-TW' ? 'B.3.2 基準減量' : 'B.3.2 Baseline emissions/removals' },
    { key: 3, label: i18n.language === 'zh-TW' ? 'B.3.3 專案減量' : 'B.3.3 Project emissions/removals' }
  ];

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB3Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 載入 GHG 選項 ==========
  useEffect(() => {
    const loadGhgOptions = async () => {
      try {
        const codes = await getSystemCodesByType('GHG');
        setGhgOptions(codes);
      } catch (err) {
        console.error('載入 GHG 選項失敗:', err);
      }
    };
    loadGhgOptions();
  }, []);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setSectionB3Data(null);
      setPropose('');
      setBaselineEmissions([]);
      setProjectEmissions([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sectionB3Service.getSectionB3ByProject(selectedProjectId);
        if (data) {
          setSectionB3Data(data);
          setPropose(data.propose || '');
          setBaselineEmissions(data.baseline_emissions || []);
          setProjectEmissions(data.project_emissions || []);
          setOriginalData({
            propose: data.propose || '',
            baseline: data.baseline_emissions || [],
            project: data.project_emissions || []
          });
        } else {
          setSectionB3Data(null);
          setPropose('');
          setBaselineEmissions([]);
          setProjectEmissions([]);
          setOriginalData({ propose: '', baseline: [], project: [] });
        }
      } catch (err: any) {
        if (err.response?.status === 404 || err.response?.status === 500) {
          setSectionB3Data(null);
          setPropose('');
          setBaselineEmissions([]);
          setProjectEmissions([]);
          setOriginalData({ propose: '', baseline: [], project: [] });
        } else {
          setError(err.response?.data?.detail || (i18n.language === 'zh-TW' ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId, i18n.language]);

  // ========== 取得目前頁籤的排放源資料 ==========
  const getCurrentEmissions = useCallback((): EmissionSource[] => {
    return activeTab === 2 ? baselineEmissions : projectEmissions;
  }, [activeTab, baselineEmissions, projectEmissions]);

  const setCurrentEmissions = useCallback((data: EmissionSource[]) => {
    if (activeTab === 2) {
      setBaselineEmissions(data);
    } else {
      setProjectEmissions(data);
    }
  }, [activeTab]);

  // ========== 建立新排放源的預設 GHG ==========
  const createDefaultGhg = (): (GhgItem & { _selected?: boolean })[] => {
    return ghgOptions.map(opt => ({
      gas_id: opt.id,
      is_included: false,
      justification: '',
      _selected: true
    }));
  };

  // ========== 取得 GHG 名稱 ==========
  const getGasName = (gasId: number): string => {
    const code = ghgOptions.find(o => o.id === gasId);
    if (!code) return String(gasId);
    return i18n.language === 'zh-TW'
      ? (code.code_cname || code.code_ename || String(gasId))
      : (code.code_ename || code.code_cname || String(gasId));
  };

  // ========== Modal 操作 ==========
  const openModal = (index?: number) => {
    const emissions = getCurrentEmissions();
    if (index !== undefined && index < emissions.length) {
      const item = emissions[index];
      setEditingIndex(index);
      setModalSource(item.source);
      // 編輯時：已有的 GHG 標記 _selected=true，沒有的帶入但 _selected=false
      const existingGasIds = item.ghg.map(g => g.gas_id);
      const allGhg = ghgOptions.map(opt => {
        const existing = item.ghg.find(g => g.gas_id === opt.id);
        if (existing) {
          return { ...existing, _selected: true };
        }
        return { gas_id: opt.id, is_included: false, justification: '', _selected: false };
      });
      setModalGhg(allGhg);
    } else {
      setEditingIndex(null);
      setModalSource('');
      setModalGhg(createDefaultGhg());
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingIndex(null);
  };

  // ========== modalGhg 操作（避免 .map 內 inline handler 的 stale closure 問題） ==========
  const updateModalGhg = (idx: number, patch: Partial<GhgItem & { _selected?: boolean }>) => {
    setModalGhg(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...patch };
      return updated;
    });
  };

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalSource.trim()) {
      toast.warning(i18n.language === 'zh-TW' ? '請輸入排放源名稱' : 'Please enter source name');
      return;
    }

    const emissions = [...getCurrentEmissions()];
    // 只保留勾選的 GHG，並移除 _selected 暫存欄位
    const filteredGhg: GhgItem[] = modalGhg
      .filter((g: any) => g._selected !== false)
      .map(({ gas_id, is_included, justification }) => ({ gas_id, is_included, justification }));
    const newItem: EmissionSource = { source: modalSource.trim(), ghg: filteredGhg };

    if (editingIndex !== null) {
      emissions[editingIndex] = newItem;
    } else {
      emissions.push(newItem);
    }

    setCurrentEmissions(emissions);
    closeModal();
  };

  const handleDeleteSource = async (index: number) => {
    const ok = await toast.confirm({
      title: i18n.language === 'zh-TW' ? '刪除確認' : 'Delete Confirmation',
      message: i18n.language === 'zh-TW' ? '確定要刪除此排放源嗎？' : 'Are you sure you want to delete this source?',
      confirmText: i18n.language === 'zh-TW' ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    const emissions = [...getCurrentEmissions()];
    emissions.splice(index, 1);
    setCurrentEmissions(emissions);
  };

  // ========== 儲存（依照目前頁籤個別儲存） ==========
  const handleSave = async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);

      const updateData: any = {};
      if (activeTab === 1) {
        updateData.propose = propose || undefined;
      } else if (activeTab === 2) {
        updateData.baseline_emissions = baselineEmissions;
      } else if (activeTab === 3) {
        updateData.project_emissions = projectEmissions;
      }

      const updatedData = await sectionB3Service.updateSectionB3(selectedProjectId, updateData);

      setSectionB3Data(updatedData);
      // 更新對應頁籤的原始資料
      if (activeTab === 1) {
        setOriginalData(prev => ({ ...prev, propose: updatedData.propose || '' }));
      } else if (activeTab === 2) {
        setOriginalData(prev => ({ ...prev, baseline: updatedData.baseline_emissions || [] }));
      } else if (activeTab === 3) {
        setOriginalData(prev => ({ ...prev, project: updatedData.project_emissions || [] }));
      }
      toast.success(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || (i18n.language === 'zh-TW' ? '儲存失敗' : 'Save failed');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ========== 重置（依照目前頁籤個別重置） ==========
  const handleReset = () => {
    if (activeTab === 1) {
      setPropose(originalData.propose);
    } else if (activeTab === 2) {
      setBaselineEmissions(JSON.parse(JSON.stringify(originalData.baseline)));
    } else if (activeTab === 3) {
      setProjectEmissions(JSON.parse(JSON.stringify(originalData.project)));
    }
    setError(null);
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }
  if (!hasPermission('section_b3', 'read')) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  }

  const canEdit = hasPermission('section_b3', 'update');
  const currentEmissions = getCurrentEmissions();

  // ========== 渲染排放源表格 ==========
  const renderEmissionsTable = () => {
    if (currentEmissions.length === 0) {
      return (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW'
            ? '尚無資料，請點擊「新增」按鈕新增排放源'
            : 'No data yet. Click "Create" to add a source.'}
        </div>
      );
    }

    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead className="table-header-dark-green">
            <tr>
              <th style={{ width: '20%' }}>{i18n.language === 'zh-TW' ? '排放源/碳庫' : 'Source/reservoir/pool'}</th>
              <th style={{ width: '10%' }}>{i18n.language === 'zh-TW' ? '溫室氣體' : 'GHG'}</th>
              <th style={{ width: '15%' }}>{i18n.language === 'zh-TW' ? '是否包含' : 'Included'}</th>
              <th style={{ width: '45%' }}>{i18n.language === 'zh-TW' ? '說明' : 'Justification/Explanation'}</th>
              <th style={{ width: '10%' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {currentEmissions.map((item, srcIndex) => (
              <React.Fragment key={srcIndex}>
                {item.ghg.map((g, ghgIndex) => (
                  <tr key={`${srcIndex}-${ghgIndex}`}>
                    {ghgIndex === 0 && (
                      <td rowSpan={item.ghg.length} style={{ verticalAlign: 'middle', fontWeight: 600 }}>
                        {item.source}
                      </td>
                    )}
                    <td>{getGasName(g.gas_id)}</td>
                    <td>
                      <span className={`status-badge ${g.is_included ? 'active' : 'inactive'}`}>
                        {g.is_included
                          ? (i18n.language === 'zh-TW' ? '包含' : 'Included')
                          : (i18n.language === 'zh-TW' ? '不包含' : 'Not included')}
                      </span>
                    </td>
                    <td dangerouslySetInnerHTML={{ __html: g.justification || '-' }} />
                    {ghgIndex === 0 && (
                      <td rowSpan={item.ghg.length} className="actions" style={{ verticalAlign: 'middle' }}>
                        {canEdit && (
                          <>
                            <button className="btn-edit" onClick={() => openModal(srcIndex)} style={{ marginRight: '8px' }}>
                              {t('common.edit')}
                            </button>
                            <button className="btn-delete" onClick={() => handleDeleteSource(srcIndex)}>
                              {t('common.delete')}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b3" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {selectedProjectId && (
        <div>
          {/* 頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px' }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                  background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                  color: activeTab === tab.key ? '#4A90E2' : '#666',
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '-2px'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* 頁籤1：減量目標（富文本） */}
              {activeTab === 1 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      {i18n.language === 'zh-TW' ? '減量目標與目的' : 'The project boundary of the proposed project'}
                    </label>
                    <RichTextEditor
                      value={propose}
                      onChange={(html) => setPropose(html)}
                      placeholder={i18n.language === 'zh-TW' ? '請輸入減量目標...' : 'Please enter project boundary...'}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              )}

              {/* 頁籤2/3：排放源 CRUD */}
              {(activeTab === 2 || activeTab === 3) && (
                <div>
                  {canEdit && (
                    <div style={{ marginBottom: '12px', textAlign: 'right' }}>
                      <button className="btn-primary" onClick={() => openModal()}>
                        {t('common.create')}
                      </button>
                    </div>
                  )}
                  {renderEmissionsTable()}
                </div>
              )}

              {/* 儲存/重置按鈕 */}
              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? t('message.saving') : t('common.save')}
                  </button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>
                    {t('common.reset')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}

      {/* 新增/編輯排放源 Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '850px' }}>
            <div className="modal-header">
              <h2>
                {editingIndex !== null
                  ? (i18n.language === 'zh-TW' ? '編輯排放源' : 'Edit Source')
                  : (i18n.language === 'zh-TW' ? '新增排放源' : 'Add Source')}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleModalSubmit}>
              <div className="modal-body">
                {/* 排放源名稱：文字框整合下拉（combo box） */}
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label style={{ fontWeight: 600 }}>
                    {i18n.language === 'zh-TW' ? '排放源/碳庫名稱' : 'Source/reservoir/pool'} *
                  </label>
                  <input
                    type="text"
                    list="source-options"
                    value={modalSource}
                    onChange={(e) => setModalSource(e.target.value)}
                    placeholder={i18n.language === 'zh-TW' ? '選擇或輸入排放源名稱' : 'Select or enter source name'}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px' }}
                  />
                  <datalist id="source-options">
                    {/* 顯示本專案已有的排放源名稱（合併 baseline + project） */}
                    {Array.from(new Set([
                      ...baselineEmissions.map(e => e.source),
                      ...projectEmissions.map(e => e.source)
                    ])).filter(s => s).map((s, i) => (
                      <option key={i} value={s} />
                    ))}
                  </datalist>
                </div>

                {/* 溫室氣體：勾選方式選擇 + 包含開關 + 多行說明 */}
                <div style={{ marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? '溫室氣體設定' : 'GHG Settings'}
                </div>
                <table className="data-table" style={{ marginBottom: '16px' }}>
                  <thead>
                    <tr style={{ background: '#1a3a5c', color: '#fff' }}>
                      <th style={{ width: '5%', color: '#fff' }}></th>
                      <th style={{ width: '12%', color: '#fff' }}>{i18n.language === 'zh-TW' ? '溫室氣體' : 'GHG'}</th>
                      <th style={{ width: '20%', color: '#fff' }}>{i18n.language === 'zh-TW' ? '是否包含' : 'Included'}</th>
                      <th style={{ width: '63%', color: '#fff' }}>{i18n.language === 'zh-TW' ? '說明' : 'Justification/Explanation'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalGhg.map((g, idx) => (
                      <tr key={idx} style={{ opacity: (g as any)._selected === false ? 0.5 : 1 }}>
                        {/* 勾選：選擇此溫室氣體 */}
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={(g as any)._selected !== false}
                            onChange={(e) => updateModalGhg(idx, { _selected: e.target.checked } as any)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </td>
                        <td>{getGasName(g.gas_id)}</td>
                        {/* 包含開關 */}
                        <td>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <div
                              onClick={() => updateModalGhg(idx, { is_included: !g.is_included })}
                              style={{
                                width: '44px', height: '24px', borderRadius: '12px',
                                background: g.is_included ? '#4CAF50' : '#ccc',
                                position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
                              }}
                            >
                              <div style={{
                                width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                                position: 'absolute', top: '2px',
                                left: g.is_included ? '22px' : '2px',
                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }} />
                            </div>
                            <span style={{ fontSize: '12px', color: g.is_included ? '#4CAF50' : '#999' }}>
                              {g.is_included
                                ? (i18n.language === 'zh-TW' ? '包含' : 'Included')
                                : (i18n.language === 'zh-TW' ? '不包含' : 'Not included')}
                            </span>
                          </label>
                        </td>
                        {/* 富文本欄位（可渲染上下標，三行高度） */}
                        <td>
                          <RichTextEditor
                            value={g.justification}
                            onChange={(html) => updateModalGhg(idx, { justification: html })}
                            placeholder={i18n.language === 'zh-TW' ? '說明/理由...' : 'Justification/Explanation...'}
                            minHeight="72px"
                            toolbar="inline"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionB3Page;
